import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";

import { Loading } from "components";
import { getServiceList } from "apis";
import { ServiceCategory } from "types";
import { ServiceDTO } from "types/DTOs/service/service.dto";
import { getImageUrl } from "utils/image.util";

const categories: { emoji: string; label: string; value: ServiceCategory }[] = [
  { emoji: "💬", label: "수다·티타임", value: ServiceCategory.CHAT },
  { emoji: "🎮", label: "게임·듀오", value: ServiceCategory.GAME },
  { emoji: "🔮", label: "타로·운세", value: ServiceCategory.TAROT },
  { emoji: "💡", label: "고민·피드백", value: ServiceCategory.FEEDBACK },
  { emoji: "💻", label: "랜선 스터디", value: ServiceCategory.STUDY },
  { emoji: "✨", label: "자유주제", value: ServiceCategory.FREE },
];

const getCategoryColor = (category: ServiceCategory): string => {
  switch (category) {
    case ServiceCategory.CHAT: return "bg-pink-50 text-pink-600";
    case ServiceCategory.GAME: return "bg-purple-50 text-purple-600";
    case ServiceCategory.TAROT: return "bg-violet-50 text-violet-600";
    case ServiceCategory.FEEDBACK: return "bg-amber-50 text-amber-600";
    case ServiceCategory.STUDY: return "bg-blue-50 text-blue-600";
    case ServiceCategory.FREE: return "bg-emerald-50 text-emerald-600";
    default: return "bg-achromatic-100 text-achromatic-500";
  }
};

const isSlotAvailable = (s: any): boolean => {
  if (s.isBooked || !s.date) return false;
  const timeStr = s.endTime || s.startTime || "23:59:59";
  const slotDate = new Date(`${s.date}T${timeStr.length === 5 ? timeStr + ":00" : timeStr}`);
  return !isNaN(slotDate.getTime()) && slotDate.getTime() > Date.now();
};

const ServiceCard = ({
  service,
  onClick,
}: {
  service: ServiceDTO;
  onClick: () => void;
}) => {
  const availableSlots = service.schedules?.filter(isSlotAvailable).length ?? 0;
  const mainImage = service.images && service.images.length > 0 ? service.images[0] : null;
  const imgUrl = mainImage ? getImageUrl(mainImage) : null;

  return (
    <div
      className="bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-card-hover transition-all duration-200 group border border-achromatic-50"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="w-full bg-achromatic-50" style={{ aspectRatio: '3/2' }}>
        {imgUrl ? (
          <img src={imgUrl} alt={service.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-3xl bg-gradient-to-br from-achromatic-50 to-achromatic-100">
            {service.category === ServiceCategory.CHAT ? '💬' : service.category === ServiceCategory.GAME ? '🎮' : service.category === ServiceCategory.TAROT ? '🔮' : service.category === ServiceCategory.FEEDBACK ? '💡' : service.category === ServiceCategory.STUDY ? '💻' : '✨'}
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-md ${getCategoryColor(service.category)}`}>
            {service.category}
          </span>
          <span className={`text-[11px] font-semibold ${
            availableSlots > 0 ? "text-primary-500" : "text-achromatic-300"
          }`}>
            {availableSlots > 0 ? `${availableSlots}개 가능` : "마감"}
          </span>
        </div>

        <h3 className="text-[15px] font-bold text-achromatic-900 mb-1 line-clamp-1 group-hover:text-primary-600 transition-colors">{service.title}</h3>
        <p className="text-xs text-achromatic-400 line-clamp-2 mb-3 leading-relaxed">{service.description}</p>

        <div className="flex justify-between items-center pt-3 border-t border-achromatic-50">
          <div className="flex items-center gap-2">
            {service.host?.profileImage ? (
              <img
                src={getImageUrl(service.host.profileImage)}
                alt="host"
                className="w-6 h-6 rounded-full object-cover"
              />
            ) : (
              <div className="w-6 h-6 bg-achromatic-100 rounded-full flex items-center justify-center text-[10px] font-bold text-achromatic-500">
                {service.host?.name?.charAt(0) ?? "?"}
              </div>
            )}
            <span className="text-xs text-achromatic-500">{service.host?.name ?? "호스트"}</span>
            <span className="text-[10px] text-achromatic-300">·</span>
            <span className="text-[11px] text-achromatic-300">{service.durationMinutes}분</span>
          </div>
          <div>
            <span className="text-[15px] font-extrabold text-achromatic-900">{service.price.toLocaleString()}</span>
            <span className="text-[11px] text-achromatic-400 ml-0.5">원</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Page() {
  const navigate = useNavigate();

  const { data: servicesData, isLoading } = useQuery("homeServices", () =>
    getServiceList()
  );

  const rawServices: ServiceDTO[] = servicesData?.data?.data ?? [];
  const services: ServiceDTO[] = rawServices.filter((s) => {
    const availableSlots = s.schedules?.filter(isSlotAvailable).length ?? 0;
    return availableSlots > 0;
  });

  return (
    <div className="animate-fadeUp">
      {/* Category Chips */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-4">
        {categories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => navigate(`/service?category=${cat.value}`)}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-white rounded-xl text-[13px] font-semibold text-achromatic-600 whitespace-nowrap hover:bg-achromatic-50 transition-colors"
          >
            <span>{cat.emoji}</span>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Greeting card */}
      <div className="bg-white rounded-2xl p-6 mb-5 mobile:rounded-none mobile:mb-4">
        <h1 className="text-xl font-extrabold text-achromatic-900 mb-1">
          시간과 경험을 거래해 보세요 ✨
        </h1>
        <p className="text-[13px] text-achromatic-400">원하는 서비스를 찾아 호스트와 연결하세요</p>
      </div>

      {/* Popular services */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-[15px] font-bold text-achromatic-900">인기 서비스</h2>
          <button
            onClick={() => navigate("/service")}
            className="text-xs text-achromatic-400 hover:text-achromatic-600 transition-colors"
          >
            전체보기 →
          </button>
        </div>

        {isLoading ? (
          <Loading />
        ) : services.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <span className="text-4xl block mb-3">📭</span>
            <p className="text-sm font-semibold text-achromatic-800 mb-1">아직 등록된 서비스가 없어요</p>
            <p className="text-xs text-achromatic-400">호스트가 서비스를 등록하면 여기에 표시됩니다</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
            {services.slice(0, 6).map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onClick={() => navigate(`/service/${service.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Recent list */}
      {services.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-[15px] font-bold text-achromatic-900">최근 등록</h2>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden mobile:rounded-none">
            {services.slice(0, 5).map((service, i) => {
              const mainImg = service.images && service.images.length > 0 ? service.images[0] : null;
              const imgUrl = mainImg ? getImageUrl(mainImg) : null;
              return (
              <div
                key={service.id}
                className={`flex items-center gap-3 px-5 py-3.5 cursor-pointer hover:bg-achromatic-50 transition-colors ${
                  i < Math.min(services.length - 1, 4) ? 'border-b border-achromatic-50' : ''
                }`}
                onClick={() => navigate(`/service/${service.id}`)}
              >
                {/* Thumbnail */}
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-achromatic-50 flex-shrink-0">
                  {imgUrl ? (
                    <img src={imgUrl} alt={service.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-lg bg-gradient-to-br from-achromatic-50 to-achromatic-100">
                      {service.category === ServiceCategory.CHAT ? '💬' : service.category === ServiceCategory.GAME ? '🎮' : service.category === ServiceCategory.TAROT ? '🔮' : service.category === ServiceCategory.FEEDBACK ? '💡' : service.category === ServiceCategory.STUDY ? '💻' : '✨'}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${getCategoryColor(service.category)}`}>
                      {service.category}
                    </span>
                    <h3 className="text-[13px] font-semibold text-achromatic-800 truncate">{service.title}</h3>
                  </div>
                  <span className="text-[11px] text-achromatic-400">{service.host?.name ?? "호스트"} · {service.durationMinutes}분</span>
                </div>
                <span className="text-[14px] font-bold text-achromatic-900 flex-shrink-0">{service.price.toLocaleString()}원</span>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
